import moment from 'moment';
import difference from 'lodash/difference';

import {SectionItem} from 'common/components/items-menu/types';
import {
  BasicFunnelStep,
  DateKeyedFunnelSegments,
  FunnelData,
  FunnelSegment,
  FunnelStep,
  SegmentId,
  SelectMode,
  TrendUnit,
} from 'common/report/models/funnels/types';
import {SegmentedFunnelResults} from 'common/report/data/funnels/api-types';
import {OVERALL_PROP, OVERALL_PROP_NAME, SEGMENT_SEPARATOR} from 'common/report/models/funnels/constants';
import {
  DEFAULT_SELECTED_SEGMENTS_LIMIT,
  MAX_SELECTED_SEGMENTS_LIMIT,
  ProjectFeatureFlag,
  QUERY_LIMIT,
  ReportName,
} from 'common/report/constants';
import {renamePropertyOld} from 'common/report/util/properties';
import {abbreviateNumber3SigFigs} from 'common/util';

import {ColorChoice, maybeAssignColors} from './colors';

export const UNIT_IN_SECONDS = {
  month: 30 * 24 * 60 * 60,
  week: 7 * 24 * 60 * 60,
  day: 24 * 60 * 60,
  hour: 60 * 60,
  minute: 60,
  second: 1,
};

const BUCKET_SIZES = [
  1,
  2,
  3,
  4,
  5,
  10,
  20,
  30,
  1 * UNIT_IN_SECONDS.minute,
  2 * UNIT_IN_SECONDS.minute,
  3 * UNIT_IN_SECONDS.minute,
  4 * UNIT_IN_SECONDS.minute,
  5 * UNIT_IN_SECONDS.minute,
  10 * UNIT_IN_SECONDS.minute,
  20 * UNIT_IN_SECONDS.minute,
  30 * UNIT_IN_SECONDS.minute,
  1 * UNIT_IN_SECONDS.hour,
  2 * UNIT_IN_SECONDS.hour,
  3 * UNIT_IN_SECONDS.hour,
  4 * UNIT_IN_SECONDS.hour,
  5 * UNIT_IN_SECONDS.hour,
  6 * UNIT_IN_SECONDS.hour,
  12 * UNIT_IN_SECONDS.hour,
  1 * UNIT_IN_SECONDS.day,
  2 * UNIT_IN_SECONDS.day,
  3 * UNIT_IN_SECONDS.day,
  4 * UNIT_IN_SECONDS.day,
  5 * UNIT_IN_SECONDS.day,
  10 * UNIT_IN_SECONDS.day,
  20 * UNIT_IN_SECONDS.day,
  30 * UNIT_IN_SECONDS.day,
];

const LOWER_NUM_BUCKETS = 12;
const UPPER_NUM_BUCKETS = 24;
const NUM_BUCKET_LIMIT = 90;

/**
 * Given a date string, returns a date string in full date format.
 * Full date formats prevent the browser from imposing its own timezone assumptions when using the Date object.
 */
export function formatDateString(date: string): string {
  if (moment(date, `YYYY-MM-DD`, true).isValid()) {
    date = date.concat(`T00:00:00`);
  }
  return date;
}

export function formatPercent(decimal, precision = 1, appendPct = true, fixedPoint = false) {
  let pct: number | string = Math.round(decimal * Math.pow(10, precision + 2)) / Math.pow(10, precision);
  if (fixedPoint) {
    pct = pct.toFixed(precision);
  }
  return appendPct ? pct + `%` : pct;
}

/** Returns the max number of segments for a query. */
export function getDefaultSegmentationLimit(context): number {
  return context.hasProjectFeatureFlag(ProjectFeatureFlag.ShowUberWelcome) ||
    context.hasProjectFeatureFlag(ProjectFeatureFlag.ShowUberWelcomeProd)
    ? 50
    : QUERY_LIMIT[ReportName.Funnels];
}

/** Creates the 'Overall' FunnelSegment using the Overall prop and propName */
export function generateOverallSegment(steps: FunnelData = []): FunnelSegment {
  steps = steps.map(addIndex);
  return {prop: OVERALL_PROP, propName: OVERALL_PROP_NAME, props: [{prop: OVERALL_PROP}], steps};
}

/** Flatten nested segment props into single array of segments. */
export function collectNestedSegments(nestedSegments: SegmentedFunnelResults, props: string[] = []): FunnelSegment[] {
  const segmentsResult = Object.entries(nestedSegments).map(([key, value]) => {
    if (isFunnelStepArray(value)) {
      const segmentProps = props.concat(key).map((prop) => ({prop}));
      const segmentProp = segmentProps.map((seg) => seg.prop).join(SEGMENT_SEPARATOR);
      const segmentPropName = getCombinedPropsAsDisplayName(segmentProps, segmentProp);
      return {
        props: segmentProps,
        prop: segmentProp,
        propName: segmentPropName,
        steps: value.map(addIndex),
      };
    } else {
      return collectNestedSegments(value, props.concat(key));
    }
  });

  return segmentsResult.flat();
}

function isFunnelStepArray(d: SegmentedFunnelResults['segment']): d is Array<BasicFunnelStep | FunnelStep> {
  return Array.isArray(d);
}

/** Combine individual segment props into one combined propName to be displayed */
function getCombinedPropsAsDisplayName(segmentProps: SegmentId[], segmentProp: string): string {
  let segmentPropName = ` `;
  if (segmentProp === OVERALL_PROP) {
    segmentPropName = OVERALL_PROP_NAME;
  } else {
    // TODO: Once segment renaming for funnels is added to common,
    // propName should be the result of concatenated, RENAMED props joined by the SEGMENT_SEPERATOR
    const concatProps = segmentProps.map((seg) => seg.prop).join(SEGMENT_SEPARATOR);
    segmentPropName = concatProps !== `` ? concatProps : segmentPropName;
  }
  return segmentPropName;
}

function addIndex<T>(item: T, index: number): T & {index: number} {
  return {...item, index};
}

/**
 * Given an array of segments and a number of segments to select, returns an array containing the prop values of the
 * first segments in the given array up to the number of segments to select.
 *
 * @param topSegments array of funnel segments, the first segments will be the ones whose prop values are returned
 * @param numSelections the number of segment prop values to return from the beginning of the given array
 * @return {string[]} array of the prop values from the beginning of the segment array up to the numSelections provided
 */
export function getDefaultSelectedSegments(topSegments: FunnelSegment[], numSelections: number): string[] {
  const segmentProps = topSegments.map((segment) => segment.prop);
  return segmentProps.slice(0, numSelections);
}

export function getSelectedSegments(
  selected: ColorChoice[] = [],
  selectMode: SelectMode = SelectMode.Position,
  topSegments: FunnelSegment[],
): ColorChoice[] {
  let selectedKeys = selected.map((choice) => choice.key);
  // if the selected segments are no longer in the top choices, invalidate the segments
  const propSet = new Set(topSegments.map((seg) => seg.prop));
  const invalidateSelected = !selectedKeys.every((key) => propSet.has(key));
  // Positional means take either top DEFAULT_SEGMENTS or top MAX_SEGMENTS
  if (invalidateSelected || selectMode === SelectMode.Position) {
    selectedKeys = getDefaultSelectedSegments(
      topSegments,
      !invalidateSelected && selectedKeys.length === MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels]
        ? MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels]
        : DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
    );
  }

  return maybeAssignColors(selected, selectedKeys);
}

/**
 * Given an array of date keyed segments and a number of segments to select, returns an array containing the prop values of the
 * first segments of the first date in the given array up to the number of segments to select.
 *
 * @param topSegments array of date keyed funnel segments, the first segments will be the ones whose prop values are returned
 * @param numSelections the number of segment prop values to return from the beginning of the given array
 * @return {string[]} array of the prop values from the beginning of the segment array up to the numSelections provided
 */
export function getDefaultSelectedTrends(topTrends: DateKeyedFunnelSegments[], numSelections: number): string[] {
  let defaultSelected = [];
  if (topTrends.length > 0) {
    defaultSelected = getDefaultSelectedSegments(topTrends[0].segments, numSelections);
  }
  return defaultSelected;
}

/**
 * Given a conversion window, returns an appropriate bucket size in seconds
 * such that LOWER_NUM_BUCKETS < #buckets < UPPER_NUM_BUCKETS
 * @param {number} conversionWindow - conversion window length in seconds
 * @returns {number} bucket size in seconds, default 1 second
 */
export function getBucketSize(conversionWindow) {
  return BUCKET_SIZES.reduce((selectedBucket, bucketSize) => {
    const numBuckets = Math.ceil(conversionWindow / (1.0 * bucketSize));
    return LOWER_NUM_BUCKETS <= numBuckets && numBuckets <= UPPER_NUM_BUCKETS ? bucketSize : selectedBucket;
  }, 1);
}

export function getAllowedTrendUnits(numDays: number): TrendUnit[] {
  const allowedTrendUnits = [];
  // numDays = 1 allows the query to work between one day and the next which is really 2 days
  // To limit minute to a 24 hour window, numDay should be 0
  if (numDays <= 0) {
    allowedTrendUnits.push(TrendUnit.Minute);
  }
  // In order to have about the NUM_BUCKET_LIMIT for hours, we found NUM_BUCKET_LIMIT/24 and then rounded up to the closest unit which is about a week (7 days)
  if (numDays <= 7) {
    allowedTrendUnits.push(TrendUnit.Hour);
  }
  if (numDays <= NUM_BUCKET_LIMIT) {
    allowedTrendUnits.push(TrendUnit.Day);
  }
  if (numDays <= NUM_BUCKET_LIMIT * 7) {
    allowedTrendUnits.push(TrendUnit.Week);
  }
  allowedTrendUnits.push(TrendUnit.Month);
  allowedTrendUnits.push(TrendUnit.Quarter);
  return allowedTrendUnits;
}

/**
 * convert a raw string to an enum value if it exists, else return a default
 * @param value raw string value to convert
 * @param enumMap enum type
 * @param defaultMember default instance of enum
 */
export function fromEnum<T>(value: string, enumType: T, defaultMember: T[keyof T]): T[keyof T] {
  if (Object.values(enumType).includes(value)) {
    return (value as unknown) as T[keyof T];
  } else {
    return defaultMember;
  }
}

/**
 * Returns an appropriate list of time buckets for the given conversion window.
 * Optimizes for showing between 12-24 evenly spaced buckets.
 * @param window the conversion window for the funnel
 */
export function getTimeBucketsFromConversionWindow(window) {
  // for sessions, approximate the conversion window length as NUM_SESSIONS * NUM_SECONDS_PER_DAY
  // (since each session is at most a day long)
  const windowUnit = window.unit === `session` ? `day` : window.unit;
  const windowSecs = window.value * UNIT_IN_SECONDS[windowUnit];

  return {
    min: 0,
    max: windowSecs,
    offset: 0,
    bucketSize: getBucketSize(windowSecs),
    isDrilledDown: false,
  };
}

export function getOverallStepsFromSegments(
  segmentData: DateKeyedFunnelSegments[],
): Array<BasicFunnelStep | FunnelStep> {
  if (!segmentData || segmentData.length === 0) {
    return [];
  }
  const {segments} = segmentData[0];
  return segments.find((s) => s.prop === OVERALL_PROP)?.steps ?? [];
}

export function getOverallConversionRateFromSegments(segmentData: DateKeyedFunnelSegments[]): number {
  return getOverallStepsFromSegments(segmentData).slice(-1)[0]?.overall_conv_ratio;
}

export function getHighestConversionRateFromSegments(segmentData: DateKeyedFunnelSegments[]): number {
  const {segments} = segmentData[0];
  return Math.max(
    ...segments.map((item) =>
      item.steps[0].count > 0 ? item.steps[item.steps.length - 1].count / item.steps[0].count : 0,
    ),
  );
}

export function isSegmented(segmentData: DateKeyedFunnelSegments[]) {
  return segmentData?.[0]?.segments.length > 1;
}

export function hasData(segmentData: DateKeyedFunnelSegments[]) {
  return segmentData?.some(({segments}) => segments?.some((segment) => segment.steps[0].count > 0));
}

export function getCountTypeText({funnelsCountType, dataGroupId}) {
  if (funnelsCountType === `unique`) {
    return dataGroupId ? `uniques` : `unique users`;
  } else {
    return `total times`;
  }
}

export function convertSegmentLabelForCohort(label: string, segmentNames: Array<string | string[]>): string {
  return label
    .split(SEGMENT_SEPARATOR)
    .map((segmentValue, idx) => ({
      value: segmentValue,
      title: renamePropertyOld(segmentNames[idx]),
    }))
    .map((segment) => `${segment.title}: ${segment.value}`)
    .join(SEGMENT_SEPARATOR);
}

export function legendLabelsFromFunnelsSegments(
  selectedSegments: ColorChoice[],
  segmentData: DateKeyedFunnelSegments[],
  comparisonSegmentData: DateKeyedFunnelSegments[],
): SectionItem[] {
  const sections: SectionItem[] = [];

  const conversionFromFunnel = (segment) => {
    const conversion = segment.steps[segment.steps.length - 1].overall_conv_ratio * 100;
    return abbreviateNumber3SigFigs(conversion);
  };

  const segments = segmentData[0].segments;
  for (let i = 0; i < selectedSegments.length; i++) {
    // Find the last corresponding segment
    const segmentKey = selectedSegments[i].key;

    const lastSegment = segments.find((value) => value.prop === segmentKey);
    const conversion = conversionFromFunnel(lastSegment);

    let label = `${lastSegment.propName} • ${conversion}%`;

    if (comparisonSegmentData) {
      const compareSegment = Object.values(comparisonSegmentData)[0].segments.find(
        (value) => value.prop === segmentKey,
      );
      const previousConversion = conversionFromFunnel(compareSegment);

      label += ` (${previousConversion}%)`;
    }

    sections.push({
      color: selectedSegments[i].color,
      label,
    });
  }

  return sections;
}

export function computeSelectionsOnSortChange({
  prevSelected,
  prevSelectMode,
  orderedRowIds,
  maxNumSelections,
  defaultNumSelections,
}: {
  prevSelected: string[];
  prevSelectMode: SelectMode;
  orderedRowIds: string[];
  maxNumSelections: number;
  defaultNumSelections: number;
}): {selected: string[]; selectMode: SelectMode} {
  let selected: string[];
  let selectMode: SelectMode;

  const rowsIncludeAllSelected = difference(prevSelected, orderedRowIds).length === 0;
  const selectedSegmentsPresent = prevSelectMode === SelectMode.Item && rowsIncludeAllSelected;
  const setToDefault = !prevSelectMode || prevSelectMode === SelectMode.Position || !selectedSegmentsPresent;
  if (setToDefault) {
    const useSelectedLength = prevSelectMode === SelectMode.Position && prevSelected.length === maxNumSelections;
    const limit = useSelectedLength ? prevSelected.length : defaultNumSelections;
    selected = orderedRowIds.slice(0, limit);
    selectMode = SelectMode.Position;
  } else {
    selected = [...prevSelected];
    selectMode = SelectMode.Item;
  }
  return {selected, selectMode};
}

export function computeSelectionsOnSelectionChange({
  prevSelected,
  orderedRowIds,
  isToggleAll,
  changed,
  maxNumSelections,
}: {
  prevSelected: string[];
  orderedRowIds: string[];
  isToggleAll: boolean;
  changed: {[id: string]: boolean};
  maxNumSelections: number;
}): {selected: string[]; selectMode: SelectMode} {
  let selected: string[];
  let selectMode: SelectMode;

  if (isToggleAll) {
    if (prevSelected.length) {
      selected = [];
      selectMode = SelectMode.Position;
    } else {
      selected = orderedRowIds.slice(0, maxNumSelections);
      selectMode = SelectMode.Position;
    }
  } else {
    selectMode = SelectMode.Item;
    const changeSet = new Set(prevSelected);
    for (const [id, isSelected] of Object.entries(changed)) {
      if (isSelected) {
        changeSet.add(id);
      } else {
        changeSet.delete(id);
      }
    }
    // TODO: this, but more efficiently.
    // Need to keep the order of selected segments the same as in the table.
    selected = Array.from(changeSet).sort((a, b) => {
      return orderedRowIds.findIndex((id) => id === a) - orderedRowIds.findIndex((id) => id === b);
    });
  }

  return {selected, selectMode};
}
